import { useEffect, useState } from 'react';
import { FaArrowLeft, FaCalendarAlt, FaCar, FaCheck, FaCog, FaEdit, FaGasPump, FaMapMarkerAlt, FaPalette, FaPlus, FaTachometerAlt, FaTrash } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getApiUrl } from '../../config/api.config';

const CarDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Modal states
  const [showBuyerModal, setShowBuyerModal] = useState(false);
  const [showTestRideModal, setShowTestRideModal] = useState(false);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  
  // Form states
  const [buyerForm, setBuyerForm] = useState({
    name: '', phone: '', email: '', address: '',
    soldPrice: '', soldDate: '', paymentMode: 'Cash',
    financeBank: '', notes: ''
  });
  
  const [testRideForm, setTestRideForm] = useState({
    name: '', phone: '', email: '', date: '',
    feedback: '', interested: false
  });
  
  const [inquiryForm, setInquiryForm] = useState({
    name: '', phone: '', email: '', inquiryType: 'Walk-in',
    date: '', notes: '', followUpRequired: false, followUpDate: ''
  });

  useEffect(() => {
    fetchCarDetails();
  }, [id]);

  const getImageSrc = (image) => {
    if (!image) return '';
    return typeof image === 'string' ? image : (image.url || image.uri || image.secure_url || image.path || '');
  };

  const fetchCarDetails = async () => {
    try {
      if (!id || id === 'undefined' || id === 'null') {
        toast.error('Invalid car id');
        navigate('/dashboard/cars');
        return;
      }

      const token = localStorage.getItem('agent_token');
      const response = await fetch(getApiUrl(`/cars/${id}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCar(data.data.car);
      } else {
        toast.error('Failed to fetch car details');
        navigate('/dashboard/cars');
      }
    } catch (error) {
      console.error('Error fetching car details:', error);
      toast.error('Error fetching car details');
      navigate('/dashboard/cars');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this car?')) return;

    try {
      const token = localStorage.getItem('agent_token');
      const response = await fetch(getApiUrl(`/cars/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success('Car deleted successfully');
        navigate('/dashboard/cars');
      } else {
        toast.error('Failed to delete car');
      }
    } catch (error) {
      console.error('Error deleting car:', error);
      toast.error('Error deleting car');
    }
  };

  const handleMarkAsSold = async () => {
    if (!buyerForm.name || !buyerForm.phone || !buyerForm.soldPrice) {
      toast.error('Please fill in buyer name, phone, and sold price');
      return;
    }

    try {
      const token = localStorage.getItem('agent_token');
      const response = await fetch(getApiUrl(`/cars/${id}/mark-sold`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          buyerInfo: {
            ...buyerForm,
            soldDate: buyerForm.soldDate || new Date().toISOString()
          }
        })
      });

      if (response.ok) {
        toast.success('Car marked as sold successfully!');
        setShowBuyerModal(false);
        fetchCarDetails();
        setBuyerForm({
          name: '', phone: '', email: '', address: '',
          soldPrice: '', soldDate: '', paymentMode: 'Cash',
          financeBank: '', notes: ''
        });
      } else {
        const data = await response.json();
        toast.error(data.message || 'Failed to mark car as sold');
      }
    } catch (error) {
      console.error('Error marking car as sold:', error);
      toast.error('Error marking car as sold');
    }
  };

  const handleAddTestRide = async () => {
    if (!testRideForm.name || !testRideForm.phone) {
      toast.error('Please fill in customer name and phone');
      return;
    }

    try {
      const token = localStorage.getItem('agent_token');
      const response = await fetch(getApiUrl(`/cars/${id}/test-ride`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...testRideForm,
          date: testRideForm.date || new Date().toISOString()
        })
      });

      if (response.ok) {
        toast.success('Test ride customer added successfully!');
        setShowTestRideModal(false);
        fetchCarDetails();
        setTestRideForm({
          name: '', phone: '', email: '', date: '',
          feedback: '', interested: false
        });
      } else {
        const data = await response.json();
        toast.error(data.message || 'Failed to add test ride customer');
      }
    } catch (error) {
      console.error('Error adding test ride customer:', error);
      toast.error('Error adding test ride customer');
    }
  };

  const handleAddInquiry = async () => {
    if (!inquiryForm.name || !inquiryForm.phone) {
      toast.error('Please fill in customer name and phone');
      return;
    }

    try {
      const token = localStorage.getItem('agent_token');
      const response = await fetch(getApiUrl(`/cars/${id}/inquiry`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...inquiryForm,
          date: inquiryForm.date || new Date().toISOString()
        })
      });

      if (response.ok) {
        toast.success('Inquiry customer added successfully!');
        setShowInquiryModal(false);
        fetchCarDetails();
        setInquiryForm({
          name: '', phone: '', email: '', inquiryType: 'Walk-in',
          date: '', notes: '', followUpRequired: false, followUpDate: ''
        });
      } else {
        const data = await response.json();
        toast.error(data.message || 'Failed to add inquiry customer');
      }
    } catch (error) {
      console.error('Error adding inquiry customer:', error);
      toast.error('Error adding inquiry customer');
    }
  };

  const handleTogglePrivate = async () => {
    try {
      const token = localStorage.getItem('agent_token');
      const response = await fetch(getApiUrl(`/cars/${id}/toggle-private`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchCarDetails();
      } else {
        const data = await response.json();
        toast.error(data.message || 'Failed to toggle privacy');
      }
    } catch (error) {
      console.error('Error toggling privacy:', error);
      toast.error('Error toggling privacy');
    }
  };

  const nextImage = () => {
    if (car?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % car.images.length);
    }
  };

  const prevImage = () => {
    if (car?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + car.images.length) % car.images.length);
    }
  };

  // Gallery modal keyboard navigation and body overflow handling
  useEffect(() => {
    if (!showGalleryModal) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setShowGalleryModal(false);
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [showGalleryModal, currentImageIndex]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!car) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">Car not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard/cars')}
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <FaArrowLeft className="w-5 h-5 mr-2" />
                Back to Cars
              </button>
              <h1 className="text-2xl font-bold text-gray-900">{car.title}</h1>
            </div>
            <div className="flex items-center space-x-3">
              {car.status !== 'sold' && !car.buyerInfo?.name && (
                <button
                  onClick={() => setShowBuyerModal(true)}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <FaCheck className="w-4 h-4 mr-2" />
                  Mark as Sold
                </button>
              )}
              <button
                onClick={handleTogglePrivate}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                  car.isPrivate 
                    ? 'bg-yellow-600 text-white hover:bg-yellow-700' 
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
                title={car.isPrivate ? 'Currently hidden from public' : 'Currently visible to public'}
              >
                {car.isPrivate ? '🔒 Make Public' : '🔓 Make Private'}
              </button>
              <button
                onClick={() => navigate(`/dashboard/cars/edit/${id}`)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <FaEdit className="w-4 h-4 mr-2" />
                Edit
              </button>
              <button
                onClick={handleDelete}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <FaTrash className="w-4 h-4 mr-2" />
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">        {/* Status Banner */}
        {car.buyerInfo?.name && (
          <div className="mb-6 bg-green-100 border-2 border-green-300 rounded-lg p-4">
            <div className="flex items-center">
              <FaCheck className="w-5 h-5 text-green-600 mr-2" />
              <span className="font-semibold text-green-800">
                This car has been SOLD to {car.buyerInfo.name} on {new Date(car.buyerInfo.soldDate).toLocaleDateString()}
              </span>
            </div>
          </div>
        )}

        {/* Private Status Banner */}
        {car.isPrivate && (
          <div className="mb-6 bg-purple-100 border-2 border-purple-300 rounded-lg p-4">
            <div className="flex items-center">
              <span className="text-2xl mr-2">🔒</span>
              <span className="font-semibold text-purple-800">
                This car is PRIVATE and hidden from public listings. Only you can see it.
              </span>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mb-6 flex space-x-4">
          <button
            onClick={() => setShowTestRideModal(true)}
            className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <FaPlus className="w-4 h-4 mr-2" />
            Add Test Ride Customer
          </button>
          <button
            onClick={() => setShowInquiryModal(true)}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <FaPlus className="w-4 h-4 mr-2" />
            Add Inquiry Customer
          </button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
          {/* Image Gallery */}
          <div className="flex flex-col space-y-4 h-full">
            <div className="relative bg-white rounded-lg shadow-sm overflow-hidden flex-1 flex flex-col">
              {car.images && car.images.length > 0 ? (
                <>
                  <img
                    src={getImageSrc(car.images[currentImageIndex])}
                    alt={`${car.title} - Image ${currentImageIndex + 1}`}
                    onError={(e) => { e.currentTarget.src = 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400'; }}
                    onClick={() => setShowGalleryModal(true)}
                    className="w-full h-96 lg:h-full object-cover flex-1 cursor-pointer hover:opacity-95 transition"
                  />
                  {car.images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all"
                      >
                        ‹
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all"
                      >
                        ›
                      </button>
                      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                        {car.images.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentImageIndex(index)}
                            className={`w-2 h-2 rounded-full transition-all ${
                              index === currentImageIndex ? 'bg-white' : 'bg-white bg-opacity-50'
                            }`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-80 lg:h-full bg-gray-200 flex-1 flex items-center justify-center">
                  <FaCar className="w-16 h-16 text-gray-400" />
                </div>
              )}
            </div>

            {/* Thumbnail Gallery */}
            {car.images && car.images.length > 1 && (
              <div className="flex space-x-2 overflow-x-auto mt-2 pb-2">
                {car.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => { setCurrentImageIndex(index); setShowGalleryModal(true); }}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      index === currentImageIndex ? 'border-blue-500' : 'border-gray-200'
                    }`}
                  >
                    <img
                      src={getImageSrc(image)}
                      alt={`Thumbnail ${index + 1}`}
                      onError={(e) => { e.currentTarget.src = 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400'; }}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Car Details */}
          <div className="space-y-6">
            {/* Price */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-gray-900">₹{car.price?.toLocaleString()}</p>
                  <p className="text-sm text-gray-500">Listed Price</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold text-green-600">
                    ₹{(car.price * 0.9)?.toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-500">Negotiable</p>
                </div>
              </div>
            </div>

            {/* Key Specs */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Specifications</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-3">
                  <FaCalendarAlt className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Year</p>
                    <p className="font-medium">{car.year}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <FaTachometerAlt className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Mileage</p>
                    <p className="font-medium">{car.kmDriven?.toLocaleString()} km</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <FaGasPump className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Fuel</p>
                    <p className="font-medium">{car.fuelType}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <FaCog className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Transmission</p>
                    <p className="font-medium">{car.transmission}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <FaPalette className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Color</p>
                    <p className="font-medium">{car.color}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <FaMapMarkerAlt className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="font-medium">{car.location?.city}, {car.location?.state}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            {car.description && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Description</h3>
                <p className="text-gray-700 leading-relaxed">{car.description}</p>
              </div>
            )}

            {/* Features */}
            {car.features && car.features.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Features</h3>
                <div className="flex flex-wrap gap-2">
                  {car.features.map((feature, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Seller Information */}
            {car.seller && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Seller Information</h3>
                <div className="space-y-2">
                  <p className="text-gray-700"><span className="font-medium">Name:</span> {car.seller?.name || 'N/A'}</p>
                  <p className="text-gray-700"><span className="font-medium">Phone:</span> {car.seller?.phoneNumber || 'N/A'}</p>
                  <p className="text-gray-700"><span className="font-medium">Email:</span> {car.seller?.email || 'N/A'}</p>
                </div>
              </div>
            )}

            {/* Private Details - Only visible to agents */}
            {car.privateDetails && (
              <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-lg shadow-sm p-6 border-2 border-red-200">
                <div className="flex items-center space-x-2 mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">🔒 Private Details</h3>
                  <span className="px-2 py-1 text-xs font-medium text-red-600 bg-red-100 border border-red-300 rounded-full">
                    Confidential
                  </span>
                </div>
                
                {/* Owner Information */}
                {(car.privateDetails.ownerName || car.privateDetails.ownerPhone || car.privateDetails.ownerEmail || car.privateDetails.ownerAddress) && (
                  <div className="mb-4 pb-4 border-b border-red-200">
                    <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                      <span className="mr-2">👤</span> Owner Information
                    </h4>
                    <div className="space-y-1 text-sm">
                      {car.privateDetails.ownerName && (
                        <p className="text-gray-700"><span className="font-medium">Name:</span> {car.privateDetails.ownerName}</p>
                      )}
                      {car.privateDetails.ownerPhone && (
                        <p className="text-gray-700"><span className="font-medium">Phone:</span> {car.privateDetails.ownerPhone}</p>
                      )}
                      {car.privateDetails.ownerEmail && (
                        <p className="text-gray-700"><span className="font-medium">Email:</span> {car.privateDetails.ownerEmail}</p>
                      )}
                      {car.privateDetails.ownerAddress && (
                        <p className="text-gray-700"><span className="font-medium">Address:</span> {car.privateDetails.ownerAddress}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Insurance Details */}
                {(car.privateDetails.insuranceProvider || car.privateDetails.insurancePolicyNumber || car.privateDetails.insuranceExpiryDate) && (
                  <div className="mb-4 pb-4 border-b border-red-200">
                    <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                      <span className="mr-2">🛡️</span> Insurance Details
                    </h4>
                    <div className="space-y-1 text-sm">
                      {car.privateDetails.insuranceProvider && (
                        <p className="text-gray-700"><span className="font-medium">Provider:</span> {car.privateDetails.insuranceProvider}</p>
                      )}
                      {car.privateDetails.insurancePolicyNumber && (
                        <p className="text-gray-700"><span className="font-medium">Policy #:</span> {car.privateDetails.insurancePolicyNumber}</p>
                      )}
                      {car.privateDetails.insuranceExpiryDate && (
                        <p className="text-gray-700"><span className="font-medium">Expiry:</span> {new Date(car.privateDetails.insuranceExpiryDate).toLocaleDateString()}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Vehicle Documents */}
                {(car.privateDetails.rcNumber || car.privateDetails.chassisNumber || car.privateDetails.engineNumber) && (
                  <div className="mb-4 pb-4 border-b border-red-200">
                    <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                      <span className="mr-2">📄</span> Vehicle Documents
                    </h4>
                    <div className="space-y-1 text-sm">
                      {car.privateDetails.rcNumber && (
                        <p className="text-gray-700"><span className="font-medium">RC Number:</span> {car.privateDetails.rcNumber}</p>
                      )}
                      {car.privateDetails.chassisNumber && (
                        <p className="text-gray-700"><span className="font-medium">Chassis:</span> {car.privateDetails.chassisNumber}</p>
                      )}
                      {car.privateDetails.engineNumber && (
                        <p className="text-gray-700"><span className="font-medium">Engine:</span> {car.privateDetails.engineNumber}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Finance Details */}
                {(car.privateDetails.bankFinanced || car.privateDetails.bankName || car.privateDetails.loanOutstanding !== undefined || car.privateDetails.noc !== undefined) && (
                  <div className="mb-4 pb-4 border-b border-red-200">
                    <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                      <span className="mr-2">🏦</span> Finance Details
                    </h4>
                    <div className="space-y-1 text-sm">
                      <p className="text-gray-700">
                        <span className="font-medium">Bank Financed:</span> 
                        <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${car.privateDetails.bankFinanced ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                          {car.privateDetails.bankFinanced ? 'Yes' : 'No'}
                        </span>
                      </p>
                      {car.privateDetails.bankName && (
                        <p className="text-gray-700"><span className="font-medium">Bank:</span> {car.privateDetails.bankName}</p>
                      )}
                      {car.privateDetails.loanOutstanding > 0 && (
                        <p className="text-gray-700"><span className="font-medium">Outstanding:</span> ₹{car.privateDetails.loanOutstanding.toLocaleString()}</p>
                      )}
                      <p className="text-gray-700">
                        <span className="font-medium">NOC Available:</span> 
                        <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${car.privateDetails.noc ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {car.privateDetails.noc ? 'Yes' : 'No'}
                        </span>
                      </p>
                    </div>
                  </div>
                )}

                {/* Service & Purchase History */}
                {(car.privateDetails.lastServiceDate || car.privateDetails.serviceCenter || car.privateDetails.acquisitionDate || car.privateDetails.purchasePrice) && (
                  <div className="mb-4 pb-4 border-b border-red-200">
                    <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                      <span className="mr-2">🔧</span> Service & Purchase History
                    </h4>
                    <div className="space-y-1 text-sm">
                      {car.privateDetails.lastServiceDate && (
                        <p className="text-gray-700"><span className="font-medium">Last Service:</span> {new Date(car.privateDetails.lastServiceDate).toLocaleDateString()}</p>
                      )}
                      {car.privateDetails.serviceCenter && (
                        <p className="text-gray-700"><span className="font-medium">Service Center:</span> {car.privateDetails.serviceCenter}</p>
                      )}
                      {car.privateDetails.acquisitionDate && (
                        <p className="text-gray-700"><span className="font-medium">Acquired On:</span> {new Date(car.privateDetails.acquisitionDate).toLocaleDateString()}</p>
                      )}
                      {car.privateDetails.purchasePrice && (
                        <p className="text-gray-700">
                          <span className="font-medium">Purchase Price:</span> ₹{car.privateDetails.purchasePrice.toLocaleString()}
                          {car.price && car.privateDetails.purchasePrice && (
                            <span className="ml-2 text-green-600 font-semibold">
                              (Margin: ₹{(car.price - car.privateDetails.purchasePrice).toLocaleString()})
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Additional Notes */}
                {car.privateDetails.additionalNotes && (
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                      <span className="mr-2">📝</span> Additional Private Notes
                    </h4>
                    <p className="text-sm text-gray-700 bg-white bg-opacity-60 p-3 rounded-lg border border-red-200">
                      {car.privateDetails.additionalNotes}
                    </p>
                  </div>
                )}

                {/* No Private Details Message */}
                {!car.privateDetails.ownerName && !car.privateDetails.ownerPhone && !car.privateDetails.insuranceProvider && 
                 !car.privateDetails.rcNumber && !car.privateDetails.bankFinanced && !car.privateDetails.lastServiceDate && 
                 !car.privateDetails.additionalNotes && (
                  <p className="text-sm text-gray-500 italic">No private details available for this car.</p>
                )}
              </div>
            )}

            {/* Private Documents - Only visible to agents */}
            {car.privateDocuments && car.privateDocuments.length > 0 && (
              <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-lg shadow-sm p-6 border-2 border-red-200">
                <div className="flex items-center space-x-2 mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">🔒 Private Documents</h3>
                  <span className="px-2 py-1 text-xs font-medium text-red-600 bg-red-100 border border-red-300 rounded-full">
                    Confidential - {car.privateDocuments.length} {car.privateDocuments.length === 1 ? 'Document' : 'Documents'}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                  {car.privateDocuments.map((doc, index) => (
                    <div
                      key={index}
                      className="relative bg-white rounded-lg border-2 border-red-200 p-3 hover:shadow-lg transition-shadow cursor-pointer"
                      onClick={() => {
                        setSelectedDoc(doc);
                        setShowDocModal(true);
                      }}
                    >
                      {doc.type && doc.type.startsWith('image/') ? (
                        <img
                          src={doc.url}
                          alt={doc.name || `Document ${index + 1}`}
                          className="w-full h-32 object-cover rounded hover:opacity-80"
                        />
                      ) : (
                        <div 
                          className="w-full h-32 flex flex-col items-center justify-center bg-red-50 rounded hover:bg-red-100"
                        >
                          <svg className="w-12 h-12 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                          </svg>
                          <p className="text-xs text-gray-600 mt-2">Click to view</p>
                        </div>
                      )}
                      <p className="text-xs text-gray-700 mt-2 truncate font-medium" title={doc.name || `Document ${index + 1}`}>
                        {doc.name || `Document ${index + 1}`}
                      </p>
                      {doc.uploadedAt && (
                        <p className="text-xs text-gray-500">
                          {new Date(doc.uploadedAt).toLocaleDateString()}
                        </p>
                      )}
                      <div className="absolute top-2 right-2 px-2 py-0.5 bg-red-600 text-white text-xs rounded-full flex items-center">
                        🔒 Private
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Buyer Information Section */}
        {car.buyerInfo?.name && (
          <div className="mt-8 bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">💰 Buyer Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><span className="font-medium">Name:</span> {car.buyerInfo.name}</div>
              <div><span className="font-medium">Phone:</span> {car.buyerInfo.phone}</div>
              {car.buyerInfo.email && <div><span className="font-medium">Email:</span> {car.buyerInfo.email}</div>}
              {car.buyerInfo.address && <div><span className="font-medium">Address:</span> {car.buyerInfo.address}</div>}
              <div><span className="font-medium">Sold Price:</span> ₹{car.buyerInfo.soldPrice?.toLocaleString()}</div>
              <div><span className="font-medium">Sold Date:</span> {new Date(car.buyerInfo.soldDate).toLocaleDateString()}</div>
              <div><span className="font-medium">Payment Mode:</span> {car.buyerInfo.paymentMode}</div>
              {car.buyerInfo.financeBank && <div><span className="font-medium">Finance Bank:</span> {car.buyerInfo.financeBank}</div>}
              {car.buyerInfo.notes && <div className="col-span-2"><span className="font-medium">Notes:</span> {car.buyerInfo.notes}</div>}
            </div>
          </div>
        )}

        {/* Test Ride & Inquiry Customers */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
          {/* Test Ride Customers */}
          <div className="bg-white rounded-lg shadow-sm p-6 h-full flex flex-col">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">🚗 Test Ride Customers ({car.testRideCustomers?.length || 0})</h2>
            <div className="flex-1 overflow-y-auto pr-2">
              {car.testRideCustomers && car.testRideCustomers.length > 0 ? (
                <div className="space-y-4">
                  {car.testRideCustomers.map((customer, index) => (
                    <div key={index} className={`border rounded-lg p-4 hover:bg-gray-50 ${customer.source === 'BuyerApp' ? 'border-orange-300 bg-orange-50' : 'border-gray-200'}`}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Name:</span> {customer.name}
                          {customer.source === 'BuyerApp' && (
                            <span className="px-2 py-0.5 text-xs font-semibold text-orange-700 bg-orange-100 rounded-full">📱 BuyerApp</span>
                          )}
                        </div>
                        <div><span className="font-medium">Phone:</span> {customer.phone}</div>
                        {customer.email && <div><span className="font-medium">Email:</span> {customer.email}</div>}
                        <div><span className="font-medium">Date:</span> {new Date(customer.date).toLocaleDateString()}</div>
                        {customer.interested !== undefined && (
                          <div>
                            <span className="font-medium">Interested:</span> 
                            <span className={`ml-2 px-2 py-1 rounded-full text-xs ${customer.interested ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                              {customer.interested ? 'Yes' : 'No'}
                            </span>
                          </div>
                        )}
                        {customer.feedback && <div className="col-span-2"><span className="font-medium">Feedback:</span> {customer.feedback}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 italic">No test ride customers yet</p>
              )}
            </div>
          </div>

          {/* Inquiry Customers */}
          <div className="bg-white rounded-lg shadow-sm p-6 h-full flex flex-col">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">📞 Inquiry Customers ({car.inquiryCustomers?.length || 0})</h2>
            <div className="flex-1 overflow-y-auto pr-2">
              {car.inquiryCustomers && car.inquiryCustomers.length > 0 ? (
                <div className="space-y-4">
                  {car.inquiryCustomers.map((customer, index) => (
                    <div key={index} className={`border rounded-lg p-4 hover:bg-gray-50 ${customer.source === 'BuyerApp' ? 'border-orange-300 bg-orange-50' : 'border-gray-200'}`}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Name:</span> {customer.name}
                          {customer.source === 'BuyerApp' && (
                            <span className="px-2 py-0.5 text-xs font-semibold text-orange-700 bg-orange-100 rounded-full">📱 BuyerApp</span>
                          )}
                        </div>
                        <div><span className="font-medium">Phone:</span> {customer.phone}</div>
                        {customer.email && <div><span className="font-medium">Email:</span> {customer.email}</div>}
                        <div><span className="font-medium">Inquiry Type:</span> {customer.inquiryType}</div>
                        <div><span className="font-medium">Date:</span> {new Date(customer.date).toLocaleDateString()}</div>
                        {customer.followUpRequired && customer.followUpDate && (
                          <div>
                            <span className="font-medium">Follow-up:</span> 
                            <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                              {new Date(customer.followUpDate).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                        {customer.notes && <div className="col-span-2"><span className="font-medium">Notes:</span> {customer.notes}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 italic">No inquiry customers yet</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Private Document Modal */}
      {showDocModal && selectedDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4" onClick={() => setShowDocModal(false)}>
          <button
            className="absolute top-4 right-4 text-white text-3xl p-2 rounded-full hover:bg-white hover:bg-opacity-10 z-50"
            onClick={(e) => { e.stopPropagation(); setShowDocModal(false); }}
            aria-label="Close"
          >
            ×
          </button>
          
          <div className="max-w-5xl w-full max-h-[90vh] flex flex-col items-center justify-center" onClick={(e) => e.stopPropagation()}>
            {selectedDoc.type && selectedDoc.type.startsWith('image/') ? (
              <img
                src={selectedDoc.url}
                alt={selectedDoc.name || 'Private Document'}
                className="w-full h-auto max-h-[90vh] object-contain rounded-lg shadow-lg"
              />
            ) : (
              <div className="bg-white rounded-lg p-8 max-w-2xl w-full">
                <div className="text-center">
                  <svg className="w-24 h-24 text-red-600 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                  </svg>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{selectedDoc.name || 'Private Document'}</h3>
                  <p className="text-gray-600 mb-6">This document type cannot be previewed in browser</p>
                  <a
                    href={selectedDoc.url}
                    download={selectedDoc.name || 'document'}
                    className="inline-flex items-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download Document
                  </a>
                </div>
              </div>
            )}
            
            <div className="mt-4 bg-black bg-opacity-60 rounded-lg px-4 py-2 text-white">
              <p className="font-semibold">{selectedDoc.name || 'Private Document'}</p>
              {selectedDoc.uploadedAt && (
                <p className="text-sm text-gray-300">
                  Uploaded: {new Date(selectedDoc.uploadedAt).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Gallery Modal */}
      {showGalleryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4" onClick={() => setShowGalleryModal(false)}>
          <button
            className="absolute top-4 right-4 text-white text-3xl p-2 rounded-full hover:bg-white hover:bg-opacity-10"
            onClick={(e) => { e.stopPropagation(); setShowGalleryModal(false); }}
            aria-label="Close gallery"
          >
            ×
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-4 text-white text-4xl p-2 rounded-full hover:bg-white hover:bg-opacity-10"
            aria-label="Previous image"
          >
            ‹
          </button>
          <div className="max-w-5xl w-full max-h-[90vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={getImageSrc(car.images[currentImageIndex])}
              alt={`${car.title} - Image ${currentImageIndex + 1}`}
              onError={(e) => { e.currentTarget.src = 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400'; }}
              className="w-full h-auto max-h-[90vh] object-contain rounded-lg shadow-lg"
            />
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-4 text-white text-4xl p-2 rounded-full hover:bg-white hover:bg-opacity-10"
            aria-label="Next image"
          >
            ›
          </button>
        </div>
      )}

      {/* Buyer Modal */}
      {showBuyerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Mark Car as Sold</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Buyer Name *</label>
                    <input
                      type="text"
                      value={buyerForm.name}
                      onChange={(e) => setBuyerForm({...buyerForm, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                    <input
                      type="tel"
                      value={buyerForm.phone}
                      onChange={(e) => setBuyerForm({...buyerForm, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={buyerForm.email}
                      onChange={(e) => setBuyerForm({...buyerForm, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sold Price (₹) *</label>
                    <input
                      type="number"
                      value={buyerForm.soldPrice}
                      onChange={(e) => setBuyerForm({...buyerForm, soldPrice: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sold Date</label>
                    <input
                      type="date"
                      value={buyerForm.soldDate}
                      onChange={(e) => setBuyerForm({...buyerForm, soldDate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode</label>
                    <select
                      value={buyerForm.paymentMode}
                      onChange={(e) => setBuyerForm({...buyerForm, paymentMode: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      <option value="Cash">Cash</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Cheque">Cheque</option>
                      <option value="Finance">Finance</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
                {buyerForm.paymentMode === 'Finance' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Finance Bank</label>
                    <input
                      type="text"
                      value={buyerForm.financeBank}
                      onChange={(e) => setBuyerForm({...buyerForm, financeBank: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <textarea
                    value={buyerForm.address}
                    onChange={(e) => setBuyerForm({...buyerForm, address: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={buyerForm.notes}
                    onChange={(e) => setBuyerForm({...buyerForm, notes: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    rows={3}
                    maxLength={500}
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowBuyerModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleMarkAsSold}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Mark as Sold
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test Ride Modal */}
      {showTestRideModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-xl w-full">
            <div className="p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Add Test Ride Customer</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name *</label>
                    <input
                      type="text"
                      value={testRideForm.name}
                      onChange={(e) => setTestRideForm({...testRideForm, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                    <input
                      type="tel"
                      value={testRideForm.phone}
                      onChange={(e) => setTestRideForm({...testRideForm, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={testRideForm.email}
                      onChange={(e) => setTestRideForm({...testRideForm, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <input
                      type="date"
                      value={testRideForm.date}
                      onChange={(e) => setTestRideForm({...testRideForm, date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={testRideForm.interested}
                      onChange={(e) => setTestRideForm({...testRideForm, interested: e.target.checked})}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Customer showed interest</span>
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Feedback</label>
                  <textarea
                    value={testRideForm.feedback}
                    onChange={(e) => setTestRideForm({...testRideForm, feedback: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    rows={3}
                    maxLength={300}
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowTestRideModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddTestRide}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Add Customer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inquiry Modal */}
      {showInquiryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-xl w-full">
            <div className="p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Add Inquiry Customer</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name *</label>
                    <input
                      type="text"
                      value={inquiryForm.name}
                      onChange={(e) => setInquiryForm({...inquiryForm, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                    <input
                      type="tel"
                      value={inquiryForm.phone}
                      onChange={(e) => setInquiryForm({...inquiryForm, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={inquiryForm.email}
                      onChange={(e) => setInquiryForm({...inquiryForm, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Inquiry Type</label>
                    <select
                      value={inquiryForm.inquiryType}
                      onChange={(e) => setInquiryForm({...inquiryForm, inquiryType: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="Walk-in">Walk-in</option>
                      <option value="Phone Call">Phone Call</option>
                      <option value="WhatsApp">WhatsApp</option>
                      <option value="Email">Email</option>
                      <option value="Website">Website</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <input
                      type="date"
                      value={inquiryForm.date}
                      onChange={(e) => setInquiryForm({...inquiryForm, date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={inquiryForm.followUpRequired}
                      onChange={(e) => setInquiryForm({...inquiryForm, followUpRequired: e.target.checked})}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Follow-up required</span>
                  </label>
                </div>
                {inquiryForm.followUpRequired && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up Date</label>
                    <input
                      type="date"
                      value={inquiryForm.followUpDate}
                      onChange={(e) => setInquiryForm({...inquiryForm, followUpDate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={inquiryForm.notes}
                    onChange={(e) => setInquiryForm({...inquiryForm, notes: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    rows={3}
                    maxLength={300}
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowInquiryModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddInquiry}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Add Customer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarDetails;